import {
	AfterViewInit,
	Component,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { PublicTableComponent } from '@componentes/public-table/public-table.component';
import { ClimatologiaResponseDTO } from '@home/submodulos/dados-meteorologicos/interfaces/climatologia';
import {
	Estacao,
	Municipio,
} from '@home/submodulos/dados-meteorologicos/interfaces/filtros-opcoes';
import { PostosRelatorios } from '@home/submodulos/dados-meteorologicos/interfaces/tabela-relatorio';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
	PeriodosLabel,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import {
	capitalizeFirstLetter,
	compararStrings,
	numberToBrNumber,
	isNotNuloOuUndefined,
	enumAsSelectOptions,
} from '@utils';
import { ADTColumns } from 'angular-datatables/src/models/settings';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { Select } from '@layout/interfaces/select';
import { Agrupamento, AgrupamentoEspecial, Meses } from '../../../interfaces';
import {
	exportarCSV,
	exportarPDF,
	exportarTXT,
	exportarXLSX,
} from '@home/submodulos/dados-meteorologicos/utils/exportacao-tabelas';
import { configsColunasCoordenadas } from '../../../utils/configs-colunas-exportacao';

interface MesSelecionado {
	n: number;
	mes: string;
	mostrar: boolean;
}
interface ResultadoAgrupado {
	nome: string;
	total: number;
	[key: string]: any;
}

@Component({
	selector: 'seira-tabela-climatologia',
	templateUrl: './tabela-climatologia.component.html',
	styleUrls: ['./tabela-climatologia.component.scss'],
})
export class TabelaClimatologiaComponent
	implements AfterViewInit, OnInit, OnDestroy, InstanciaRelatorio
{
	@ViewChild('tabelaClimatologia', { static: false })
	tabela: PublicTableComponent;

	postos: PostosRelatorios[] = [];
	estacoes: Estacao[] = [];
	inputs = inject(INPUTS_RELATORIOS);
	dadosTabelaClimatologia?: any[];
	loading = true;
	periodo = '';
	tipoAgrupamento: string = Agrupamento.MUNICIPIO_POSTO;
	tituloTabela = '';
	descricaoRelatorio =
		'Define-se como climatologia pluviométrica, a média histórica de pluviometria para períodos longos, preferencialmente de 30 anos.';

	dadosColunas: any[] = [];
	colunas: ADTColumns[] = [];
	_destroyed = new Subject<void>();
	colunasSelecionadas: string[] = [];
	opcoesColunas = [
		{ name: 'Município/Posto', isFixed: true },
		{ name: 'Latitude', isFixed: false },
		{ name: 'Longitude', isFixed: false },
		{ name: 'Altitude', isFixed: false },
		{ name: 'Microrregião', isFixed: false },
		{ name: 'Mesorregião', isFixed: false },
		{ name: 'Região Pluviométrica', isFixed: false },
		{ name: 'Bacia/Sub-bacia', isFixed: false },
	];

	meses: MesSelecionado[] = [
		{ n: 1, mes: 'Janeiro', mostrar: false },
		{ n: 2, mes: 'Fevereiro', mostrar: false },
		{ n: 3, mes: 'Março', mostrar: false },
		{ n: 4, mes: 'Abril', mostrar: false },
		{ n: 5, mes: 'Maio', mostrar: false },
		{ n: 6, mes: 'Junho', mostrar: false },
		{ n: 7, mes: 'Julho', mostrar: false },
		{ n: 8, mes: 'Agosto', mostrar: false },
		{ n: 9, mes: 'Setembro', mostrar: false },
		{ n: 10, mes: 'Outubro', mostrar: false },
		{ n: 11, mes: 'Novembro', mostrar: false },
		{ n: 12, mes: 'Dezembro', mostrar: false },
	];

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () =>
				exportarPDF({
					resultados: this.dadosTabelaClimatologia ?? [],
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					colunasAEsquerda:
						this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value ===
						'MUNICIPIO_POSTO'
							? [0]
							: [],
				}),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () =>
				exportarCSV({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
				}),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () =>
				exportarTXT({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					titulo: this.tituloTabela,
				}),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () =>
				exportarXLSX({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					colunasAEsquerda:
						this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value ===
						'MUNICIPIO_POSTO'
							? [0]
							: [],
					configsColunas: [
						...configsColunasCoordenadas,
						...this.meses.map(({ mes }) => ({
							casasDecimais: 1,
							coluna: mes.toLowerCase(),
						})),
						{ coluna: 'total', casasDecimais: 1 },
					],
				}),
		},
	];

	disabledSelectList = false;
	tituloColunaAgrupamento = 'Nome';
	periodoTitulo = '';
	periodoAgrupamento = '';
	form?: FormGroup<any>;
	regioes?: Select<string>[];
	microrregioes?: Select<string>[];
	mesorregioes?: Select<string>[];
	municipios?: Municipio[];
	precipitacao?: Select<string>[];
	agrupamento: Select<string>[];
	municipioPostos: Select<string>[];

	constructor(
		private toastrService: ToastrService,
		private relatoriosService: RelatoriosService
	) {
		this.postos = this.inputs.postos;
		this.estacoes = this.inputs.estacoes;
		this.municipioPostos = this.inputs.municipioPostos;
		this.colunasSelecionadas = this.opcoesColunas
			.filter(option => option.isFixed)
			.map(option => option.name);
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
		this.setValidators();
	}

	ngAfterViewInit(): void {
		this.inputs.form
			?.get(FormularioRelatorio.ESTACAO)
			?.setValue(this.municipioPostos[0].value);
	}

	ngOnInit(): void {
		const agrCtrl = this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO);

		// Estado inicial
		this.onAgrupamentoChange(agrCtrl?.value as Agrupamento);

		// Reagir às mudanças do agrupamento
		agrCtrl?.valueChanges
			.pipe(takeUntil(this._destroyed))
			.subscribe((valor: Agrupamento) => {
				this.onAgrupamentoChange(valor);
			});
	}

	private onAgrupamentoChange(novoAgrupamento: Agrupamento | string) {
		this.tipoAgrupamento = novoAgrupamento as Agrupamento;
		this.disabledSelectList =
			this.tipoAgrupamento !== Agrupamento.MUNICIPIO_POSTO;
		this.atualizarTituloColuna();
	}

	setColunasSelecionadas(colunas: string[]) {
		this.colunasSelecionadas = colunas;
		if (this.dadosTabelaClimatologia) {
			this.gerarRelatorio();
		}
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.MES_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MES_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this._destroyed.next();
		this._destroyed.complete();
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.MES_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.MES_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	atualizarTituloColuna() {
		const agrupamento = this.getFormItemValue(FormularioRelatorio.AGRUPAMENTO);
		this.tituloColunaAgrupamento =
			AgrupamentoEspecial[agrupamento as keyof typeof AgrupamentoEspecial];
	}

	atualizarColunasTabela() {
		const agrupamento = this.getFormItemValue(FormularioRelatorio.AGRUPAMENTO);

		// Limpa as colunas atuais
		this.colunas = [];

		if (agrupamento === 'MUNICIPIO_POSTO') {
			this.disabledSelectList = false;
			const todasColunasDisponiveis = [
				{
					data: 'nome',
					title: 'Município/Posto',
					type: 'string',
					className: 'text-left',
				},
				{
					data: 'latitude',
					title: 'Latitude',
					type: 'number',
					className: 'text-center',
				},
				{
					data: 'longitude',
					title: 'Longitude',
					type: 'number',
					className: 'text-center',
				},
				{
					data: 'altitude',
					title: 'Altitude',
					type: 'number',
					className: 'text-center',
				},
				{
					data: 'microrregiao',
					title: 'Microrregião',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'mesorregiao',
					title: 'Mesorregião',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'regiao',
					title: 'Região Pluviométrica',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'bacia',
					title: 'Bacia/Sub-bacia',
					type: 'string',
					className: 'text-center',
				},
			];

			const colunasParaExibir = todasColunasDisponiveis.filter(coluna =>
				this.colunasSelecionadas.includes(coluna.title)
			);

			this.colunas = [...colunasParaExibir];
		} else {
			this.disabledSelectList = true;
			this.colunas = [
				{
					data: 'nome',
					title: this.tituloColunaAgrupamento,
					type: 'string',
					className: 'text-center',
				},
			];
		}

		// SEMPRE adicionar colunas dos meses visíveis
		this.meses
			.filter(mes => mes.mostrar)
			.forEach(mes => {
				this.colunas.push({
					data: mes.mes.toLowerCase(),
					title: mes.mes.substring(0, 3),
					className: 'text-center',
					type: 'number',
					render: (data: number) =>
						isNotNuloOuUndefined(data) ? numberToBrNumber(data, 1) : '-',
				});
			});

		// SEMPRE adicionar coluna Total
		this.colunas.push({
			data: 'total',
			title: 'Total',
			className: 'text-center',
			type: 'number',
			render: (data: number) =>
				isNotNuloOuUndefined(data) ? numberToBrNumber(data, 1) : '-',
		});
	}

	gerarRelatorio(): void {
		if (this.inputs.form.invalid) {
			return;
		}

		// Resetar todos os meses para não mostrar
		this.meses.forEach(mes => (mes.mostrar = false));

		const mesInicio = this.inputs.form.get(FormularioRelatorio.MES_INICIO)
			?.value;
		const mesFim = this.inputs.form.get(FormularioRelatorio.MES_FIM)?.value;

		if (!mesInicio || !mesFim) {
			return;
		}

		// Encontrar meses formatados com validação
		const mesInicioFormatado = Object.values(Meses).find(mes =>
			compararStrings(mes, mesInicio)
		);
		const mesFimFormatado = Object.values(Meses).find(mes =>
			compararStrings(mes, mesFim)
		);

		// Verificar se os meses foram encontrados
		if (!mesInicioFormatado || !mesFimFormatado) {
			this.toastrService.error('Mês inicial ou final inválido');
			return;
		}

		// Encontrar índices dos meses selecionados
		const inicioIdx = this.meses.findIndex(m =>
			compararStrings(m.mes, mesInicio)
		);
		const fimIdx = this.meses.findIndex(m => compararStrings(m.mes, mesFim));

		// Marcar meses no intervalo como visíveis
		for (let i = inicioIdx; i <= fimIdx; i++) {
			if (this.meses[i]) {
				this.meses[i].mostrar = true;
			}
		}

		this.periodoAgrupamento = this.getFormItemValue(
			FormularioRelatorio.PERIODO_BUSCA
		) as PeriodosLabel;

		// Ajuste para capitalizar apenas a primeira letra dos meses
		this.periodo = `${this.normalizarMes(mesInicio)} a ${this.normalizarMes(
			mesFim
		)}`;

		this.tipoAgrupamento = this.getFormItemValue(
			FormularioRelatorio.AGRUPAMENTO
		);
		this.tituloTabela = `Climatologia - ${this.periodo}`;

		this.loading = true;
		this.inputs.setLoading(true);

		const idEstacaoSelecionada =
			this.tipoAgrupamento === 'MUNICIPIO_POSTO'
				? this.getFormItemValue(FormularioRelatorio.ESTACAO)
				: undefined;

		this.relatoriosService
			.buscarClimatologias(
				this.mesParaBackendToken(mesInicioFormatado),
				this.mesParaBackendToken(mesFimFormatado),
				this.tipoAgrupamento,
				undefined,
				idEstacaoSelecionada
			)
			.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: climatologias => {
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						climatologias
					);

					// Processar dados de acordo com o tipo de agrupamento
					this.processarDadosClimatologia(climatologias);
				},
				error: () => {
					this.toastrService.error(
						'Erro ao gerar tabela de Climatologia, tente novamente'
					);
					this.inputs.setLoading(false);
					this.loading = false;
					this.dadosTabelaClimatologia = undefined;
				},
				complete: () => {
					this.inputs.setLoading(false);
					this.loading = false;
				},
			});
	}

	private processarDadosClimatologia(climatologias: ClimatologiaResponseDTO[]) {
		const agrupamento: string = this.tipoAgrupamento;

		if (agrupamento === 'MUNICIPIO_POSTO') {
			// Para agrupamento por município/posto
			this.dadosColunas = this.ordenaMes(climatologias, false);
			this.dadosTabelaClimatologia = this.padronizarResultadosMunicipioPosto(
				this.dadosColunas
			);
		} else {
			// Para outros agrupamentos (Microrregião, Mesorregião, etc.)
			this.dadosColunas = this.ordenaMes(climatologias, true);
			this.dadosTabelaClimatologia =
				this.padronizarResultadosOutrosAgrupamentos(this.dadosColunas);
		}

		this.atualizarTituloColuna();
		this.atualizarColunasTabela();
	}

	private padronizarResultadosMunicipioPosto(
		resultados: ClimatologiaResponseDTO[]
	): any[] {
		const resultadosAgrupados = new Map<string, any>();

		resultados.forEach(resultado => {
			const municipioValue = resultado.dadosPosto?.municipio || '';
			const key = `${municipioValue}-${resultado.nome}`;

			if (!resultadosAgrupados.has(key)) {
				resultadosAgrupados.set(key, {
					nome: this.formatarNomePosto(resultado.nome, resultado.dadosPosto),
					municipio: municipioValue,
					latitude: numberToBrNumber(resultado.dadosPosto?.latitude, 4) || '-',
					longitude:
						numberToBrNumber(resultado.dadosPosto?.longitude, 4) || '-',
					altitude: numberToBrNumber(resultado.dadosPosto?.altitude, 1) || '-',
					microrregiao: resultado.dadosPosto?.microrregiao || '-',
					mesorregiao: resultado.dadosPosto?.mesorregiao || '-',
					regiao: resultado.dadosPosto?.regiao || '-',
					bacia: resultado.dadosPosto?.bacia || '-',
					total: 0,
				});
			}

			const mes = this.meses.find(m => m.n === resultado.nmes);
			if (mes && mes.mostrar) {
				const entry = resultadosAgrupados.get(key)!;
				const chave = this.normalizarMes(mes.mes).toLowerCase();
				entry[chave] = resultado.climatologia;

				entry.total += resultado.climatologia || 0;
			}
		});

		return Array.from(resultadosAgrupados.values());
	}

	private padronizarResultadosOutrosAgrupamentos(
		resultados: ClimatologiaResponseDTO[]
	): any[] {
		const resultadosAgrupados = new Map<string, any>();

		resultados.forEach(resultado => {
			const nomeAgrupamento = resultado.nome;
			const key = nomeAgrupamento;

			if (!resultadosAgrupados.has(key)) {
				resultadosAgrupados.set(key, {
					nome: nomeAgrupamento,
					total: 0,
				});
			}

			const mes = this.meses.find(m => m.n === resultado.nmes);
			if (mes && mes.mostrar) {
				const entry = resultadosAgrupados.get(key)!;
				const chave = this.normalizarMes(mes.mes).toLowerCase();
				entry[chave] = resultado.climatologia;

				entry.total += resultado.climatologia || 0;
			}
		});

		return Array.from(resultadosAgrupados.values());
	}

	private formatarNomePosto(nomePosto: string, dadosPosto: any): string {
		if (nomePosto.includes('EMBRAPA') && dadosPosto?.municipio) {
			return `${dadosPosto.municipio} / EMBRAPA`;
		}
		return nomePosto === dadosPosto?.municipio
			? nomePosto
			: `${dadosPosto?.municipio || ''}/${nomePosto}`;
	}

	getFormItemValue(field: string) {
		return this.inputs.form.get(field)!.value;
	}

	private ordenaMes(dados: any[], possuiNome: boolean) {
		dados.forEach(item => {
			item.mes = this.normalizarMes(item.mes);

			const mesEncontrado = this.meses.find(m => m.mes === item.mes);
			if (mesEncontrado) {
				item.nmes = mesEncontrado.n;
			}
		});

		const dadosOrdenados = dados.sort((a, b) => {
			const municipioA = possuiNome ? a?.nome : a?.dadosPosto?.municipio;
			const municipioB = possuiNome ? b?.nome : b?.dadosPosto?.municipio;
			const nomeComparacao = municipioA.localeCompare(municipioB);

			if (nomeComparacao !== 0) {
				return nomeComparacao;
			}

			return a.nmes - b.nmes;
		});

		return dadosOrdenados;
	}
	private removerAcentos(txt: string): string {
		return txt.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
	}

	private mesParaBackendToken(mes: string): string {
		// garante: normaliza para "Março", baixa pra "março" e tira acento "marco"
		const normalizado = this.normalizarMes(mes).toLowerCase();
		return this.removerAcentos(normalizado);
	}

	private normalizarMes(mes: string): string {
		const mapa: Record<string, string> = {
			janeiro: 'Janeiro',
			fevereiro: 'Fevereiro',
			marco: 'Março',
			abril: 'Abril',
			maio: 'Maio',
			junho: 'Junho',
			julho: 'Julho',
			agosto: 'Agosto',
			setembro: 'Setembro',
			outubro: 'Outubro',
			novembro: 'Novembro',
			dezembro: 'Dezembro',
		};
		return mapa[mes.toLowerCase()] ?? capitalizeFirstLetter(mes.toLowerCase());
	}
}
